import { createContext, useState } from "react";
import { useDispatch } from "react-redux";
import { isEmpty } from "lodash";

import {
  editDiscountCouponItemService,
  createDiscountCouponItemService,
  deleteDiscountCouponItemService,
  getDiscountCouponItemsService,
  getDiscountCouponService,
  getServicesService,
} from "./../../../services/userServices";
import {
  setLoadingFalseAction,
  setLoadingTrueAction,
} from "./../../../redux/actions/loadingActions/loadingActions";
import {
  toastErrorMessage,
  toastSuccessMessage,
} from "../../../utils/toastMessage/toastMessage";
import { convertDate } from "../../../utils";

export const discountCouponAdminContext = createContext({
  discountCouponItems: [],
  setDiscountCouponItems: () => {},
  totalCount: null,
  setTotalCount: () => {},
  pageNumber: null,
  setPageNumber: () => {},
  discountCoupon: {},
  setDiscountCoupon: () => {},
  services: [],
  setServices: () => {},
  search: "",
  setSearch: () => {},
  id: "",
  setId: () => {},
  title: "",
  setTitle: () => {},
  title_output: "",
  setTitle_output: () => {},
  ref_num: "",
  setRef_num: () => {},
  single_user: null,
  setSingle_user: () => {},
  amount: "",
  setAmount: () => {},
  percent: "",
  setPercent: () => {},
  max_discount_amount: "",
  setMax_discount_amount: () => {},
  min_invoice_amount: "",
  setMin_invoice_amount: () => {},
  expiration_date: "",
  setExpiration_date: () => {},
  is_active: null,
  setIs_active: () => {},
  description: "",
  setDescription: () => {},
  service: "",
  setService: () => {},
  usage_total: "",
  setUsage_total: () => {},
  usage_per_client: "",
  setUsage_per_client: () => {},
  isShowModal: null,
  setIsShowModal: () => {},
  isShowDetailsModal: null,
  setIsShowDetailsModal: () => {},
  handleShowModal: () => {},
  handleCloseModal: () => {},
  handleShowDetailsModal: () => {},
  handleCloseDetailsModal: () => {},
  handleGetServices: () => {},
  handleGetDiscountCouponItemList: () => {},
  handleGetDiscountCouponItem: () => {},
  handleCreateDiscountCouponItem: () => {},
  handleEditDiscountCouponItem: () => {},
  handleDeleteDiscountCouponItem: () => {},
});

const DiscountCouponAdminContext = ({ children }) => {
  // Redux Dispatch
  const dispatch = useDispatch();

  const [discountCouponItems, setDiscountCouponItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [discountCoupon, setDiscountCoupon] = useState({});
  const [services, setServices] = useState([]);
  const [search, setSearch] = useState("");
  const [id, setId] = useState("");
  const [title, setTitle] = useState("");
  const [title_output, setTitle_output] = useState("");
  const [ref_num, setRef_num] = useState("");
  const [single_user, setSingle_user] = useState(false);
  const [amount, setAmount] = useState("");
  const [percent, setPercent] = useState("");
  const [max_discount_amount, setMax_discount_amount] = useState("");
  const [min_invoice_amount, setMin_invoice_amount] = useState("");
  const [expiration_date, setExpiration_date] = useState("");
  const [is_active, setIs_active] = useState(false);
  const [description, setDescription] = useState("");
  const [service, setService] = useState([]);
  const [usage_total, setUsage_total] = useState("");
  const [usage_per_client, setUsage_per_client] = useState("");
  const [isShowModal, setIsShowModal] = useState(false);
  const [isShowDetailsModal, setIsShowDetailsModal] = useState(false);

  const handleResetState = () => {
    setId("");
    setTitle("");
    setRef_num("");
    setTitle_output("");
    setSingle_user(false);
    setAmount("");
    setPercent("");
    setMax_discount_amount("");
    setMin_invoice_amount("");
    setExpiration_date("");
    setIs_active(false);
    setDescription("");
    setService([]);
    setUsage_total("");
    setUsage_per_client("");
  };

  // Create DiscountCoupon Modal Operations
  const handleShowModal = (catValue) => {
    setIsShowModal(true);
    if (catValue) {
      setId(catValue.id);
      setTitle(catValue.title);
      setRef_num(catValue.ref_num);
      setTitle_output(catValue.title_output);
      setSingle_user(catValue.single_user);
      setAmount(catValue.amount);
      setPercent(catValue.percent);
      setMax_discount_amount(catValue.max_discount_amount);
      setMin_invoice_amount(catValue.min_invoice_amount);
      setExpiration_date(catValue.expiration_date);
      setIs_active(catValue.is_active);
      setDescription(catValue.description);
      setService(
        catValue.title?.split(",")?.map((_t) => ({
          label: _t,
          value: services?.find((_s) => _s?.label === _t)?.value,
        }))
      );
      setUsage_total(catValue.usage_total);
      setUsage_per_client(catValue.usage_per_client);
    }
  };
  const handleCloseModal = () => {
    setIsShowModal(false);
    handleResetState();
  };

  // Details Discount Coupon Modal Operations
  const handleShowDetailsModal = (value) => {
    setIsShowDetailsModal(true);
    setDiscountCoupon(value);
  };
  const handleCloseDetailsModal = () => {
    setIsShowDetailsModal(false);
    setDiscountCoupon({});
  };

  // Handle Get Services Method
  const handleGetServices = async (event) => {
    event?.preventDefault();
    const objData = {
      offset: 0,
      limit: 300,
      search: "",
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getServicesService(objData);
      if (status === 200) {
        setServices(
          data?.list?.map((_value) => ({
            value: _value?.id,
            label: _value?.name,
          }))
        );
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Get Discount Coupon Item List Method
  const handleGetDiscountCouponItemList = async (event) => {
    event?.preventDefault();
    const isSearch = !isEmpty(event);

    const objData = { page: (isSearch ? 1 : pageNumber) - 1, search };

    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getDiscountCouponItemsService(objData);
      if (status === 200) {
        setDiscountCouponItems(data.list);
        setTotalCount(data.total);
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Get Discount Coupon Item Method
  const handleGetDiscountCouponItem = async (id, isEdit) => {
    const objData = { id };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getDiscountCouponService(objData);
      if (status === 200) {
        if (isEdit) {
          handleShowModal(data);
        } else {
          handleShowDetailsModal(data);
        }
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage(response?.data?.message);
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Create Discount Coupon Item Method
  const handleCreateDiscountCouponItem = async (event) => {
    event?.preventDefault();

    const objData = {
      title: handleGetNameOfServicesWithId(),
      title_output,
      ref_num,
      single_user,
      amount,
      percent: Number(percent),
      max_discount_amount: Number(max_discount_amount),
      min_invoice_amount: Number(min_invoice_amount),
      expiration_date: convertDate(expiration_date, "YYYY-M-DD HH:mm:ss"),
      is_active,
      description,
      usage_total: Number(usage_total),
      usage_per_client: Number(usage_per_client),
    };

    // Validation
    if (!title_output) return toastErrorMessage("عنوان الزامی میباشد");
    if (!ref_num) return toastErrorMessage("کد تخفیف الزامی میباشد");
    if (!amount && !percent)
      return toastErrorMessage("مقدار یا درصد الزامی میباشد");
    if (!max_discount_amount)
      return toastErrorMessage("حداکثر مبلغ تخفیف الزامی میباشد");
    if (!min_invoice_amount)
      return toastErrorMessage("حداقل مبلغ فاکتور الزامی میباشد");
    if (usage_total < 1)
      return toastErrorMessage(
        "تعداد دفعات مجاز استفاده نمیتواند کوچکتر از 1 باشد"
      );
    if (usage_per_client < usage_total)
      return toastErrorMessage(
        "تعداد کد تخفیف نمیتواند از تعداد دفعات مجاز استفاده کوچکتر باشد"
      );
    if (!objData.title)
      return toastErrorMessage("انتخاب حداقل یک دسته بندی الزامی میباشد");
    if (new Date(expiration_date) < new Date() || !expiration_date)
      return toastErrorMessage("تاریخ انقضا نمی توان از امروز کوچکتر باشد");

    try {
      dispatch(setLoadingTrueAction());
      const { status } = await createDiscountCouponItemService(objData);
      if (status === 200) {
        toastSuccessMessage("آیتم با موفقیت ایجاد شد");
        handleGetDiscountCouponItemList();
        handleCloseModal();
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Edit Discount Coupon Item Method
  const handleEditDiscountCouponItem = async (event) => {
    event?.preventDefault();

    const objData = {
      id,
      title: handleGetNameOfServicesWithId(),
      title_output,
      ref_num,
      single_user,
      amount,
      percent: Number(percent),
      max_discount_amount: Number(max_discount_amount),
      min_invoice_amount: Number(min_invoice_amount),
      expiration_date: convertDate(expiration_date, "YYYY-M-DD HH:mm:ss"),
      is_active,
      description,
      usage_total: Number(usage_total),
      usage_per_client: Number(usage_per_client),
    };

    // Validation
    if (!title_output) return toastErrorMessage("عنوان الزامی میباشد");
    if (!ref_num) return toastErrorMessage("کد تخفیف الزامی میباشد");
    if (!amount && !percent)
      return toastErrorMessage("مقدار یا درصد الزامی میباشد");
    if (!max_discount_amount)
      return toastErrorMessage("حداکثر مبلغ تخفیف الزامی میباشد");
    if (!min_invoice_amount)
      return toastErrorMessage("حداقل مبلغ فاکتور الزامی میباشد");
    if (usage_total < 1)
      return toastErrorMessage(
        "تعداد دفعات مجاز استفاده نمیتواند کوچکتر از 1 باشد"
      );
    if (usage_total < usage_per_client)
      return toastErrorMessage(
        "تعداد کد تخفیف نمیتواند از تعداد دفعات مجاز استفاده کوچکتر باشد"
      );
    if (!objData.title)
      return toastErrorMessage("انتخاب حداقل یک دسته بندی الزامی میباشد");
    if (new Date(expiration_date) < new Date() || !expiration_date)
      return toastErrorMessage("تاریخ انقضا نمی توان از امروز کوچکتر باشد");

    try {
      dispatch(setLoadingTrueAction());
      const { status } = await editDiscountCouponItemService(objData);
      if (status === 200) {
        toastSuccessMessage("آیتم با موفقیت ویرایش شد");
        handleGetDiscountCouponItemList();
        handleCloseModal();
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Delete Discount Coupon Item Method
  const handleDeleteDiscountCouponItem = async (id) => {
    const objData = {
      id,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status } = await deleteDiscountCouponItemService(objData);
      if (status === 200) {
        toastSuccessMessage(`آیتم  ${id} با موفقیت حذف شد`);
        handleGetDiscountCouponItemList();
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Get Name Of Services With Id Method
  const handleGetNameOfServicesWithId = () => {
    if (isEmpty(service)) return null;

    if (service === "all") return "all";

    const titles = service?.map((_value) => _value.label);
    return titles.toString();
  };

  handleGetNameOfServicesWithId();

  return (
    <discountCouponAdminContext.Provider
      value={{
        discountCouponItems,
        setDiscountCouponItems,
        totalCount,
        setTotalCount,
        pageNumber,
        setPageNumber,
        discountCoupon,
        setDiscountCoupon,
        services,
        setServices,
        search,
        setSearch,
        id,
        setId,
        title,
        setTitle,
        title_output,
        setTitle_output,
        ref_num,
        setRef_num,
        single_user,
        setSingle_user,
        amount,
        setAmount,
        percent,
        setPercent,
        max_discount_amount,
        setMax_discount_amount,
        min_invoice_amount,
        setMin_invoice_amount,
        expiration_date,
        setExpiration_date,
        is_active,
        setIs_active,
        description,
        setDescription,
        service,
        setService,
        usage_total,
        setUsage_total,
        usage_per_client,
        setUsage_per_client,
        isShowModal,
        setIsShowModal,
        isShowDetailsModal,
        setIsShowDetailsModal,
        handleShowDetailsModal,
        handleCloseDetailsModal,
        handleShowModal,
        handleCloseModal,
        handleGetServices,
        handleGetDiscountCouponItemList,
        handleGetDiscountCouponItem,
        handleCreateDiscountCouponItem,
        handleEditDiscountCouponItem,
        handleDeleteDiscountCouponItem,
      }}
    >
      {children}
    </discountCouponAdminContext.Provider>
  );
};

export default DiscountCouponAdminContext;
